import { useState } from "react";
import z from "zod";

import { updateAttributes, useUserAttributes, verifyAttributeUpdate } from "@skilitics/designer-auth/attributes";
import { AuthError } from "@skilitics/designer-auth/error";
import {
  Button,
  FieldText,
  FieldValidationErrorMessage,
  FormField,
  FormikForm,
  Loading,
  toast,
} from "@skilitics/designer-ui-components";
import type { FormatMessages } from "@skilitics/localization";

import { useErrorMessage } from "../../../../auth/ui/useCognitoErrorHandling.ts";
import { ButtonsWrapper, FormCell, LayoutForm, LayoutGroup } from "../styled";

type RequestValues = {
  email: string;
};

const requestSchema = z.object({
  email: z.string().trim().email({ message: "Invalid email address format" }),
});

type ConfirmValues = {
  email: string;
  code: string;
};

const confirmSchema = z.object({
  // Regex matches the minimal API-level validation, which is much too ugly to show a human:
  // "1 validation error detected: Value '...' failed to satisfy constraint: Member must
  //  satisfy regular expression pattern: [\S]+"
  code: z
    .string()
    .trim()
    .regex(/^[\S]+$/, { message: "Invalid code format" }),
});

export const EmailForm = () => {
  const errorMessage = useErrorMessage();
  const attributes = useUserAttributes();
  const [requestValues, setRequestValues] = useState<RequestValues>(() => ({
    email: attributes?.email ?? "",
  }));
  const [confirmValues, setConfirmValues] = useState<ConfirmValues | null>(null);

  return (
    <LayoutGroup>
      <FormikForm<RequestValues>
        initialValues={requestValues}
        formatStrings={formatStrings}
        validationSchema={requestSchema}
        onSubmit={async ({ email }, helpers) => {
          try {
            email = email.trim();
            // Email attribute updates require verification, so this only
            // sends a code, it doesn't actually change it yet. Docs say the
            // API returns a "delivery code details" response, that gives
            // you the explicit details, but I don't see it in practice?
            await updateAttributes({ email });
            setConfirmValues({ email, code: "" });
            toast("Please check your email for confirmation.");
          } catch (error) {
            const message = error instanceof AuthError ? errorMessage(error) : "An unknown error occurred.";
            helpers.setFieldError("email", message);
          }
        }}
        onReset={() => setConfirmValues(null)}
      >
        {({ dirty, isSubmitting }) => (
          <LayoutForm $gridColumns="400px 1fr" blurOnEnter={false}>
            <FormField<RequestValues> name="email">
              <FormCell>
                <FieldText onInput={() => setConfirmValues(null)} />
                <FieldValidationErrorMessage />
              </FormCell>
            </FormField>
            {dirty && (
              <ButtonsWrapper>
                {!confirmValues && (
                  <Button type="submit" variant="contained" disabled={isSubmitting}>
                    {isSubmitting && <Loading inline size={12} />} Update Email
                  </Button>
                )}
                {confirmValues && (
                  <Button type="submit" variant="text">
                    Resend Code
                  </Button>
                )}
                <Button type="reset" color="inherit">
                  Cancel
                </Button>
              </ButtonsWrapper>
            )}
          </LayoutForm>
        )}
      </FormikForm>
      {confirmValues && (
        <FormikForm<ConfirmValues>
          initialValues={confirmValues}
          validationSchema={confirmSchema}
          formatStrings={formatStrings}
          onSubmit={async ({ email, code }, helpers) => {
            try {
              code = code.trim();
              await verifyAttributeUpdate("email", code);
              toast.success("Email changed successfully.");
              setRequestValues({ email });
              setConfirmValues(null);
            } catch (error) {
              const message = error instanceof AuthError ? errorMessage(error) : "An unknown error occurred.";
              helpers.setFieldError("code", message);
            }
          }}
        >
          {({ dirty, isSubmitting }) => (
            <LayoutForm $gridColumns="400px 200px 40px" className="added">
              <FormField<ConfirmValues> name="code">
                <FormCell>
                  <FieldText autoFocus />
                  <FieldValidationErrorMessage />
                </FormCell>
              </FormField>
              {dirty && (
                <ButtonsWrapper>
                  <Button type="submit" variant="contained" disabled={isSubmitting}>
                    {isSubmitting && <Loading inline size={12} />} Confirm
                  </Button>
                  <Button type="reset" color="inherit">
                    Cancel
                  </Button>
                </ButtonsWrapper>
              )}
            </LayoutForm>
          )}
        </FormikForm>
      )}
    </LayoutGroup>
  );
};

const formatStrings = {
  emailLabel: { en: "Email" },
  codeLabel: { en: "Verification Code" },
} satisfies FormatMessages;
